import { useState, useCallback } from 'react';
import API_ROUTES from './apiRoutes';
import { Section } from '../Models/Section';
import { useFetch } from './useFetch';

export const useSections = () => {
    const [sections, setSections] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [success, setSuccess] = useState(false);
    const { fetchWithAuth } = useFetch();

    const getToken = () => localStorage.getItem("token");

    const resetStates = useCallback(() => {
        setLoading(false);
        setError(null);
        setSuccess(false);
    }, []);

    const fetchSections = useCallback(async () => {
        try {
            setLoading(true);
            const response = await fetchWithAuth(API_ROUTES.SECTIONS.list(), {
                method: "GET",
                headers: {
                    "Authorization": `Bearer ${getToken()}`,
                    "Content-Type": "application/json",
                }
            });
            if (!response.ok) {
                throw new Error('Falha ao carregar seções');
            }
            const data = await response.json();
            const transformedSections = data.sections.map(section => Section.fromAPI(section));
            setSections(transformedSections);
            setError(null);
            setSuccess(true);
        } catch (err) {
            setError(err.message || 'Erro ao carregar seções');
        } finally {
            setLoading(false);
        }
    }, [fetchWithAuth]);

    const createSection = useCallback(async (sectionData) => {
        resetStates();
        setLoading(true);

        try {
            const section = new Section(sectionData);
            const apiData = section.toAPI();
            
            const response = await fetchWithAuth(API_ROUTES.SECTIONS.create(), {
                method: 'POST',
                headers: {
                    "Authorization": `Bearer ${getToken()}`,
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify(apiData)
            });

            if (!response.ok) {
                throw new Error('Falha ao criar seção');
            }

            await fetchSections();
            setSuccess(true);
            return true;
        } catch (err) {
            setError(err.message || 'Erro ao criar seção');
            return false;
        } finally {
            setLoading(false);
        }
    }, [resetStates, fetchSections, fetchWithAuth]);

    const updateSection = useCallback(async (sectionId, sectionData) => {
        resetStates();
        setLoading(true);

        try {
            const section = new Section({ id: sectionId, ...sectionData });
            const response = await fetchWithAuth(API_ROUTES.SECTIONS.update(sectionId), {
                method: 'PUT',
                headers: {
                    "Authorization": `Bearer ${getToken()}`,
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify(section.toAPI())
            });

            if (!response.ok) {
                throw new Error('Falha ao atualizar seção');
            }

            await fetchSections();
            setSuccess(true);
            return true;
        } catch (err) {
            setError(err.message || 'Erro ao atualizar seção');
            return false;
        } finally {
            setLoading(false);
        }
    }, [resetStates, fetchSections, fetchWithAuth]);

    const deleteSection = useCallback(async (sectionId) => {
        resetStates();
        setLoading(true);

        try {
            const response = await fetchWithAuth(API_ROUTES.SECTIONS.delete(sectionId), {
                method: 'DELETE',
                headers: {
                    "Authorization": `Bearer ${getToken()}`,
                    'Content-Type': 'application/json',
                }
            });

            if (!response.ok) {
                throw new Error('Falha ao excluir seção');
            }

            await fetchSections();
            setSuccess(true);
            return true;
        } catch (err) {
            setError(err.message || 'Erro ao excluir seção');
            return false;
        } finally {
            setLoading(false);
        }
    }, [resetStates, fetchSections, fetchWithAuth]);

    const sortSections = useCallback(async (sectionsData) => {
        resetStates();
        setLoading(true);

        try {
            const response = await fetchWithAuth(API_ROUTES.SECTIONS.sort(), {
                method: 'PUT',
                headers: {
                    "Authorization": `Bearer ${getToken()}`,
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({ sections: sectionsData })
            });

            if (!response.ok) {
                const errorText = await response.text();
                throw new Error(`Falha ao reordenar seções: ${response.status} - ${errorText}`);
            }

            await fetchSections();
            setSuccess(true);
            return true;
        } catch (err) {
            setError(err.message || 'Erro ao reordenar seções');
            return false;
        } finally {
            setLoading(false);
        }
    }, [resetStates, fetchSections, fetchWithAuth]);

    return {
        sections,
        loading,
        error,
        success,
        createSection,
        updateSection,
        deleteSection,
        sortSections,
        refreshSections: fetchSections
    };
}; 